<template>
  <div class="d-flex flex-column mr-3">
    <RingbaIntegrationBanner v-if="showBanner" />

    <Panel
      size="small"
      loader-text="Loading Offers"
      :loading="loadingOffers"
      :show-loading-error="showLoadingError"
      :loading-error-text="loadingErrorText"
    >
      <template v-slot:header>
        <span data-cy="heading" />
      </template>
      <template v-slot:content>
        <div>
          <b-form-input 
            v-model="filterInput"
            placeholder="Search by name, category, language or traffic source type"    
            class="custom-form-input--sm col-12 col-sm-8 col-lg-4 col-xl-4 mb-3"       
          />
          <SellerOfferList
            heading-text="Approved Offers"
            :offers="filteredApprovedOffers"
            :show-ringba-integration-actions="showRingbaIntegrationActions"            
            @showApplicationRequestDetails="openApplicationRequestDetailsModal"
            @showBiddingUrl="getBiddingURLForOfferRegistration"
            @showWithdrawModal="openWithdrawApplicationModal"
            @showRejectionModal="openRejectionReasonModal"
            @showApplyToRunModal="openApplyToRunModal"
          />
          <SellerOfferList
            heading-text="Available Offers"
            :offers="filteredOtherOffers"
            :show-ringba-integration-actions="showRingbaIntegrationActions"
            class="mt-2"
            @showApplicationRequestDetails="openApplicationRequestDetailsModal"
            @showBiddingUrl="getBiddingURLForOfferRegistration"
            @showWithdrawModal="openWithdrawApplicationModal"
            @showRejectionModal="openRejectionReasonModal"
            @showApplyToRunModal="openApplyToRunModal"
          />
        </div>
      </template>
    </Panel>
   
    <SellerApplyToRunModal
      v-if="selectedOffer"
      :offer="selectedOffer"
      @hidden="handleHideModal"
      @success="getSellerAppliedOffers"
      @shown="handleModalShown"
    />

    <RejectionReasonModal
      v-if="selectedRegistration"
      :registration="selectedRegistration"
      @hidden="handleHideModal"
    />

    <SellerApplicationDetailsModal
      v-if="selectedRegistration"
      :registration="selectedRegistration"
      @hidden="handleHideModal"
      @shown="handleModalShown"
    />

    <b-modal
      v-if="selectedRegistration"
      id="confirm-withdraw-modal"
      ref="confirm-withdraw-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      :title="`Confirm withdrawal`"
      ok-variant="danger"
      ok-title="Withdraw"
      button-size="sm"
      @ok="withdrawSellerApplication"
      @hidden="handleHideModal"
    >
      <p class="mb-0">
        Are you sure you want to withdraw your application to the <strong>{{ selectedRegistrationOfferName }}</strong>?
      </p>
      <p class="mb-0">
        Withrawing your application will stop the calls traffic to the marketplace.
      </p>
    </b-modal>

    <b-modal
      id="bidding-url-modal"
      ref="bidding-url-modal"
      v-model="showBiddingUrl"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Bidding URL"
      ok-variant="primary"
      ok-title="Ok"
      button-size="sm"
      size="lg"
      auto-focus-button="ok"
      ok-only
      @ok="showBiddingUrlModal(false)"
      @hidden="handleHideModal"
    >
      <Loader v-if="loadingBiddingUrl" />
      <SellerOfferBiddingDetails
        v-else-if="sellerOfferBiddingDetails"
        :bidding-details="sellerOfferBiddingDetails"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import SellerApplyToRunModal from './SellerApplyToRunModal.vue'
import RejectionReasonModal from './RejectionReasonModal.vue'
import SellerApplicationDetailsModal from './SellerApplicationDetailsModal.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import SellerOfferApi from '@/api/seller-offer.api'
import SellerOfferBiddingDetails from './SellerOfferBiddingDetails.vue'
import { currencyFormatter } from '@/utils/formatCurrency'
import RingbaIntegrationBanner from './RingbaIntegrationBanner.vue'
import { isRingbaIntegrationEnabled } from '@/services/featureFlags'  
import SellerOfferList from './SellerOfferList.vue'

export default {
  name: 'SellerAvailableOffers',

  title: 'Available Offers',

  components: {
    SellerApplyToRunModal,
    RejectionReasonModal,
    SellerApplicationDetailsModal,
    SellerOfferBiddingDetails,
    RingbaIntegrationBanner,
    SellerOfferList
  },  

  props: {
    sellerOfferId: {
      type: String,
      default: ''
    },
  },

  data() {
    return {
      loadingOffers: false,

      showLoadingError: false,

      loading: false,

      loadingBiddingUrl: false,

      hasCheckedToken: false,
      
      selectedOffer: null,
      
      selectedRegistration: null,

      showBiddingUrl: false,

      sellerOfferBiddingDetails: null,      

      loadingErrorText: '',      

      filterInput: '',

      searchableFields: [        
        { key: 'name' },
        { key: 'category.name' },
        { key: 'language.name' },
        { key: 'allowedTrafficSourceType' }        
      ]      
    }
  },

  computed: {
    ...mapGetters('auth', [
      'accountId', 
      'ringbaAccountId'
    ]),

    ...mapGetters('account', [
      'accountHasRingbaIntegrationCompleted'
    ]),

    ...mapGetters('sellerOffer', [
      'allOffersCombined'
    ]),

    selectedRegistrationOfferName() {
      return this.selectedRegistration?.selectedOffer?.name
    },

    showRingbaIntegrationActions() {
      return isRingbaIntegrationEnabled() && this.hasCheckedToken && this.accountHasRingbaIntegrationCompleted
    },

    showBanner() {
      return isRingbaIntegrationEnabled() && this.hasCheckedToken && !this.accountHasRingbaIntegrationCompleted
    },

    filteredOffers() {
      return this.allOffersCombined.filter(item => {
        return this.searchableFields.some(field => {          
          const keyValue = field.key.split('.').reduce((obj, prop) => obj?.[prop], item)
          return keyValue.toLowerCase().includes(this.filterInput.toLowerCase())
        })
      })      
    },

    filteredApprovedOffers() {
      return this.filteredOffers.filter(offer => offer.status === 'Approved')
    },

    filteredOtherOffers() {
      return this.filteredOffers.filter(offer => offer.status !== 'Approved')
    }
  },

  watch: {
    sellerOfferId(newValue) {
      this.handleSellerOfferFromRoute(newValue)
    }
  },

  async created() {
    try {
      this.loadingOffers = true
      await Promise.all([this.getSellerAvailableOffers(), this.getSellerAppliedOffers()])

      this.handleSellerOfferFromRoute(this.sellerOfferId)
    } catch (error) {
      this.loadingErrorText = error?.response?.data?.title
    } finally {
      this.loadingOffers = false
    }

    await this.loadRingbaData()
  },

  methods: {
    ...mapActions('sellerOffer', [
      'getSellerAvailableOffers',
      'getSellerAppliedOffers',
      'withdrawParticipationRequest',
    ]),

    ...mapActions('ringbaTokens', ['fetchTokens']),

    currencyFormatter,

    async loadRingbaData() {
      try {
        await this.fetchTokens({ accountId: this.accountId })
      } catch (error) {
        // Silently handle errors
      } finally {
        // Mark that we've checked for the token
        this.hasCheckedToken = true
      }
    },

    handleSellerOfferFromRoute(sellerOfferId) {
      if (!this.sellerOfferId) {
        this.$bvModal.hide('apply-to-run-modal')
        this.$bvModal.hide('application-details-modal')
        return
      }
      
      const selectedOffer = this.allOffersCombined.find(so => so.id === sellerOfferId || so.sellerOffer?.id === sellerOfferId)

      if (!selectedOffer) {
        this.rerouteWithQueryParam()
        return
      }

      if (selectedOffer.canApplyToOffer) {
        this.openApplyToRunModal(selectedOffer)
      } else if (!selectedOffer.canApplyToOffer) {
        this.openApplicationRequestDetailsModal(selectedOffer)
      }
    },

    rerouteWithQueryParam(sellerOfferId) {
      if (!sellerOfferId) {
        this.$router.push({ name: 'SellerAvailableOffers', query: {} })
      } else {
        this.$router.push({ name: 'SellerAvailableOffers', query: {sellerOfferId} })
      }
    },    

    openApplyToRunModal(offer) {
      if (offer.hasAppliedToOffer && offer.canApplyToOffer) {
        offer = offer.sellerOffer
      }

      this.loading = true
      this.selectedOffer = { ...offer }

      setTimeout(() => {
        this.$bvModal.show('apply-to-run-modal')        
      })

      this.loading = false
    },

    openApplicationRequestDetailsModal(registration) {
      this.selectedRegistration = { ...registration }

      setTimeout(() => {
        this.$bvModal.show('application-details-modal')
      })
    },

    openWithdrawApplicationModal(registration) {
      this.selectedRegistration = { ...registration }

      setTimeout(() => {
        this.$bvModal.show('confirm-withdraw-modal')
      })
    },


    openRejectionReasonModal(registration) {
      this.selectedRegistration = { ...registration }

      setTimeout(() => {
        this.$bvModal.show('rejection-reason-modal')
      })
    },

    async withdrawSellerApplication() {
      try {
        this.loading = true

        await this.withdrawParticipationRequest(this.selectedRegistration.id)
        showSuccessMessage('Successfully withdrawn your application request')
        
        await this.getSellerAppliedOffers()
      } catch (error) {
        showErrorMessage(`${error.response?.data?.title} ${JSON.stringify(error.response?.data?.errors)}`)
      } finally {

        this.loading = false
      }
    },


    async getBiddingURLForOfferRegistration(offerRegistration) {
      try {
        this.loadingBiddingUrl = true
        this.showBiddingUrl = true

        const response = await SellerOfferApi.getSellerOfferBiddingURL(this.accountId, offerRegistration.id)

        this.sellerOfferBiddingDetails = response.data
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.loadingBiddingUrl = false
      }
    },

    showBiddingUrlModal(show) {
      this.showBiddingUrl = show
    },

    handleHideModal() {
      this.selectedRegistration = null
      this.selectedOffer = null
      this.rerouteWithQueryParam()
    },

    handleModalShown() {
      if (this.sellerOfferId) {
        return
      }

      this.rerouteWithQueryParam(this.selectedOffer?.id || this.selectedRegistration?.sellerOffer?.id)
    }
  }
}
</script>

<style lang="scss" scoped></style>
